import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eraser, Palette, Share2, X, CopyX, Plus, FileText, Rows3, Columns3, Lock, Unlock } from "lucide-react";
import { API } from "../../context/AuthContext";
import { SKINS, TRACKS } from "./skins";
import { EventCard, Marker } from "./EventCard";
import AddEventDialog from "./AddEventDialog";
import SummaryModal from "./SummaryModal";
import VaultDialog from "./VaultDialog";
import SourceViewer from "../portal/SourceViewer";
import UndatedSection from "./UndatedSection";

export { SKINS };

const ALL_ON = { support: true, safety: true, family: true, procedural: true };

export default function Timeline({ events, caseId, loadingEvents, onDelete, onClearAll, onDedupe, onEventAdded, canEdit = true, isOwner = true, vaultToken = null, onVaultToken = () => {}, onRefresh = () => {} }) {
  const [filters, setFilters] = useState(ALL_ON);
  const [strict, setStrict] = useState(true);
  const [party, setParty] = useState("all");
  const [docFilter, setDocFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sourceView, setSourceView] = useState(null);
  const [orientation, setOrientation] = useState(() => localStorage.getItem("flux_orientation") || "vertical");
  const [skinKey, setSkinKey] = useState(() => localStorage.getItem("flux_skin") || "courtroom");
  const [share, setShare] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [vaultDialog, setVaultDialog] = useState(null);
  const [pinSet, setPinSet] = useState(null);
  const skin = SKINS[skinKey];

  useEffect(() => {
    axios.get(`${API}/vault/status`).then((res) => setPinSet(res.data.pin_set)).catch(() => {});
  }, []);

  const openVault = () => {
    if (vaultToken) {
      axios.post(`${API}/vault/lock`).catch(() => {});
      onVaultToken(null);
      toast.success("Vault locked.");
    } else {
      setVaultDialog(pinSet ? "unlock" : "setup");
    }
  };

  const toggleVault = async (ev, vaulted) => {
    if (!pinSet) {
      setVaultDialog("setup");
      return;
    }
    try {
      const headers = vaultToken ? { "X-Vault-Token": vaultToken } : {};
      await axios.patch(`${API}/events/${ev.id}/vault`, { vaulted }, { headers });
      toast.success(vaulted ? "Moved to the vault — invisible until you unlock." : "Released from the vault.");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vault action failed.");
    }
  };

  useEffect(() => {
    if (!caseId) return;
    setShare(null);
    setFilters(ALL_ON);
    setParty("all");
    setDocFilter("all");
    setStatusFilter("all");
    setReviewFilter("active");
    setTypeFilter("all");
    setAccountFilter("all");
    setFromDate("");
    setToDate("");
    axios.get(`${API}/shares`, { params: { case_id: caseId } }).then((res) => {
      if (res.data && res.data.share_id) setShare(res.data);
    }).catch(() => {});
  }, [caseId]);

  const copyLink = (shareId) => {
    navigator.clipboard.writeText(`${window.location.origin}/report/${shareId}`);
    toast.success("Report link copied! Hand it to anyone — it's read-only.");
  };

  const createShare = async () => {
    try {
      const res = await axios.post(`${API}/shares`, { case_id: caseId, skin: skinKey });
      setShare(res.data);
      copyLink(res.data.share_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create the report link.");
    }
  };

  const revokeShare = async () => {
    try {
      await axios.delete(`${API}/shares/${share.share_id}`);
      setShare(null);
      toast.success("Report link revoked — it no longer works.");
    } catch {
      toast.error("Couldn't revoke the link.");
    }
  };

  const changeSkin = (key) => {
    setSkinKey(key);
    localStorage.setItem("flux_skin", key);
  };

  const changeOrientation = (o) => {
    setOrientation(o);
    localStorage.setItem("flux_orientation", o);
  };

  const soloTrack = (key) => {
    setFilters({ support: false, safety: false, family: false, procedural: false, [key]: true });
  };

  const docs = useMemo(() => [...new Set(events.map((e) => e.source_name).filter(Boolean))], [events]);
  const eventTypes = useMemo(() => [...new Set(events.map((e) => e.event_type).filter(Boolean))].sort(), [events]);
  const accounts = useMemo(() => [...new Set(events.map((e) => e.account_last4).filter(Boolean))].sort(), [events]);
  const hasReview = useMemo(() => events.some((e) => e.review_status), [events]);

  const reviewEvent = async (ev, action) => {
    try {
      await axios.post(`${API}/events/${ev.id}/review`, { action });
      toast.success(action === "approve" ? "Event verified." : "Event rejected — hidden from the active timeline.");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update the event");
    }
  };
  const openSource = (ev) => setSourceView({ documentId: ev.document_id, page: ev.source_page, quote: ev.citation });

  const visible = events.filter((ev) => {
    const trackOk = strict ? ev.tracks.every((t) => filters[t]) : ev.tracks.some((t) => filters[t]);
    const partyOk = party === "all" || (ev.party || "neutral") === party;
    const docOk = docFilter === "all" || ev.source_name === docFilter;
    const statusOk = statusFilter === "all" || ev.status === statusFilter;
    const rs = ev.review_status || "legacy";
    const reviewOk = reviewFilter === "all" || (reviewFilter === "active" ? rs !== "rejected" : rs === reviewFilter);
    const typeOk = typeFilter === "all" || ev.event_type === typeFilter;
    const accountOk = accountFilter === "all" || ev.account_last4 === accountFilter;
    const dateOk = (!fromDate || ev.date >= fromDate) && (!toDate || ev.date <= toDate);
    return trackOk && partyOk && docOk && statusOk && reviewOk && typeOk && accountOk && dateOk;
  });

  const filtersActive = !Object.values(filters).every(Boolean) || party !== "all" || docFilter !== "all" || statusFilter !== "all" || reviewFilter !== "active" || typeFilter !== "all" || accountFilter !== "all" || fromDate || toDate;
  const smallBtn = (extra) => `flex items-center gap-1.5 text-[10px] font-mono px-2.5 py-1.5 rounded-lg border transition-colors duration-200 whitespace-nowrap ${extra}`;
  const mutedBtn = skin.light ? "text-gray-600 border-black/15" : "text-gray-400 border-white/10";
  const selCls = `text-[10px] font-mono rounded-lg px-2 py-1.5 border focus:outline-none ${skin.light ? "bg-white border-black/15 text-gray-700" : "bg-gray-950 border-white/10 text-gray-300"}`;

  return (
    <div className={`border p-6 rounded-2xl shadow-xl flex flex-col min-h-[560px] transition-colors duration-300 ${skin.panel}`}>
      <div className="border-b border-current/10 pb-4 mb-4 space-y-3">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h3 className={`font-bold text-base uppercase tracking-wide ${skin.title}`}>Parallel Timeline Track Core Matrix</h3>
            <p className={`text-xs mt-0.5 ${skin.desc}`}>Isolate tracks, parties, documents or event types — or overlay them all.</p>
          </div>
          <div className={`flex items-center gap-1 border rounded-lg p-1 ${skin.light ? "bg-black/5 border-black/15" : "bg-black/20 border-white/10"}`} data-testid="skin-selector">
            <Palette className={`w-3 h-3 ml-1 ${skin.light ? "text-gray-600" : "text-gray-400"}`} />
            {Object.entries(SKINS).map(([key, s]) => (
              <button
                key={key}
                onClick={() => changeSkin(key)}
                data-testid={`skin-btn-${key}`}
                className={`px-2 py-1 rounded text-[9px] font-mono font-bold uppercase transition-colors duration-200 ${skinKey === key ? "bg-[#f59e0b] text-gray-950" : skin.light ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-white"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className={`flex items-center border rounded-lg p-0.5 ${skin.light ? "bg-black/5 border-black/15" : "bg-black/20 border-white/10"}`} data-testid="orientation-toggle">
            <button onClick={() => changeOrientation("vertical")} data-testid="orientation-vertical-btn" title="Vertical timeline" className={`p-1.5 rounded transition-colors duration-200 ${orientation === "vertical" ? "bg-[#f59e0b] text-gray-950" : skin.light ? "text-gray-600" : "text-gray-400"}`}>
              <Rows3 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => changeOrientation("horizontal")} data-testid="orientation-horizontal-btn" title="Horizontal timeline" className={`p-1.5 rounded transition-colors duration-200 ${orientation === "horizontal" ? "bg-[#f59e0b] text-gray-950" : skin.light ? "text-gray-600" : "text-gray-400"}`}>
              <Columns3 className="w-3.5 h-3.5" />
            </button>
          </div>
          {isOwner && (
            <button
              onClick={openVault}
              data-testid="vault-btn"
              title={vaultToken ? "Lock the vault" : pinSet ? "Unlock the evidence vault" : "Create a PIN-locked evidence vault"}
              className={smallBtn(vaultToken ? "font-bold text-emerald-400 border-emerald-500/40 bg-emerald-500/5" : `${mutedBtn} font-bold hover:text-[#f59e0b] hover:border-[#f59e0b]/40`)}
            >
              {vaultToken ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
              {vaultToken ? "VAULT OPEN" : "VAULT"}
            </button>
          )}
          {canEdit && (
            <button onClick={() => setShowAdd(true)} data-testid="add-event-btn" className={smallBtn("font-bold text-gray-950 bg-[#f59e0b] border-[#f59e0b] hover:bg-[#fbbf24]")}>
              <Plus className="w-3 h-3" /> ADD EVENT
            </button>
          )}
          {events.length > 0 && (
            <>
              <button onClick={() => setShowSummary(true)} data-testid="summary-btn" title="Generate a plain text chronological summary for pleadings" className={smallBtn(skin.light ? "text-purple-800 border-purple-700/40 hover:bg-purple-700/10" : "text-purple-300 border-purple-500/30 hover:border-purple-500/60")}>
                <FileText className="w-3 h-3" /> LEGAL WRITING
              </button>
              <button onClick={share ? () => copyLink(share.share_id) : createShare} data-testid="share-report-btn" title="Create a shareable, read-only report link" className={smallBtn(skin.light ? "font-bold text-teal-800 border-teal-700/50 hover:bg-teal-700/10" : "font-bold text-[#2dd4bf] hover:text-[#5eead4] border-[#2dd4bf]/30 hover:border-[#2dd4bf]/60")}>
                <Share2 className="w-3 h-3" /> {share ? "COPY REPORT LINK" : "SHARE REPORT"}
              </button>
              {share && (
                <button onClick={revokeShare} data-testid="revoke-share-btn" title="Revoke the report link" className={`p-1.5 rounded-lg border transition-colors duration-200 ${mutedBtn} hover:text-rose-400 hover:border-rose-500/30`}>
                  <X className="w-3 h-3" />
                </button>
              )}
              {canEdit && (
                <>
                  <button onClick={onDedupe} data-testid="dedupe-btn" title="Remove duplicate events from this timeline" className={smallBtn(`${mutedBtn} hover:text-[#f59e0b] hover:border-[#f59e0b]/40`)}>
                    <CopyX className="w-3 h-3" /> DEDUPE
                  </button>
                  <button onClick={onClearAll} data-testid="clear-all-btn" className={smallBtn(`${mutedBtn} hover:text-rose-400 hover:border-rose-500/40`)}>
                    <Eraser className="w-3 h-3" /> WIPE MATRIX
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px] font-mono">
        {TRACKS.map((t) => (
          <label
            key={t.key}
            onDoubleClick={() => soloTrack(t.key)}
            title="Double-click to isolate only this track"
            className={`flex items-center space-x-2 border px-3 py-1.5 rounded-lg cursor-pointer transition-all duration-200 ${skin.light ? t.lightCls : t.cls} ${filters[t.key] ? "" : "opacity-40"}`}
          >
            <input
              type="checkbox"
              checked={filters[t.key]}
              onChange={() => setFilters((f) => ({ ...f, [t.key]: !f[t.key] }))}
              data-testid={`track-filter-${t.key}`}
            />
            <span>{t.label}</span>
          </label>
        ))}
        <button
          onClick={() => setStrict((s) => !s)}
          data-testid="filter-mode-toggle"
          title={strict ? "ISOLATE: an event is hidden if ANY of its tracks is unchecked" : "OVERLAY: an event shows if ANY of its tracks is checked"}
          className={smallBtn(strict ? "text-[#f59e0b] border-[#f59e0b]/40 bg-[#f59e0b]/5 font-bold" : `${mutedBtn} font-bold`)}
        >
          {strict ? "MODE: ISOLATE" : "MODE: OVERLAY"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <select value={party} onChange={(e) => setParty(e.target.value)} className={selCls} data-testid="party-filter-select">
          <option value="all">👥 Both Parties</option>
          <option value="petitioner">Petitioner Only</option>
          <option value="respondent">Respondent Only</option>
          <option value="neutral">Neutral Only</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selCls} data-testid="status-filter-select">
          <option value="all">⚡ All Event Types</option>
          <option value="alert">Alerts / Incidents</option>
          <option value="pending">Pending / Upcoming</option>
          <option value="completed">Completed</option>
        </select>
        {docs.length > 0 && (
          <select value={docFilter} onChange={(e) => setDocFilter(e.target.value)} className={`${selCls} max-w-[220px]`} data-testid="doc-filter-select">
            <option value="all">📄 All Documents</option>
            {docs.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        {hasReview && (
          <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} className={selCls} data-testid="review-filter-select">
            <option value="active">✔ Active (hide rejected)</option>
            <option value="needs_review">Needs Review</option>
            <option value="approved">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="all">Everything</option>
          </select>
        )}
        {eventTypes.length > 0 && (
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selCls} data-testid="event-type-filter-select">
            <option value="all">🏷 All Categories</option>
            {eventTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        )}
        {accounts.length > 0 && (
          <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)} className={selCls} data-testid="account-filter-select">
            <option value="all">🏦 All Accounts</option>
            {accounts.map((a) => <option key={a} value={a}>••{a}</option>)}
          </select>
        )}
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={selCls} title="From date" data-testid="date-from-filter" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={selCls} title="To date" data-testid="date-to-filter" />
        {filtersActive && (
          <span className={`text-[10px] font-mono ${skin.desc}`} data-testid="filter-count">
            {visible.length} of {events.length} events shown
          </span>
        )}
      </div>

      {loadingEvents ? (
        <div className="text-xs text-[#f59e0b] font-mono animate-blink py-12 text-center flex-1">Professor Flux is indexing isolated timeline tracks...</div>
      ) : events.length === 0 ? (
        <div className={`text-xs italic py-12 text-center flex-1 ${skin.desc}`} data-testid="timeline-empty-state">
          Ingest unstructured memory records or data blocks inside the left workspace panel to compile timelines.
        </div>
      ) : visible.length === 0 ? (
        <div className={`text-xs italic py-12 text-center flex-1 ${skin.desc}`} data-testid="timeline-no-match">No events match the current filters. Loosen a filter or switch to OVERLAY mode.</div>
      ) : orientation === "horizontal" ? (
        <div className="flex-1 overflow-x-auto pb-4" data-testid="timeline-stream-horizontal">
          <div className="flex min-w-max pt-2">
            {visible.map((ev) => (
              <div key={ev.id} className="flex flex-col w-[280px] shrink-0">
                <div className="flex items-center w-full px-1">
                  <div className={`h-0.5 flex-1 ${skin.light ? "bg-black/15" : "bg-white/10"}`} />
                  <Marker event={ev} />
                  <div className={`h-0.5 flex-1 ${skin.light ? "bg-black/15" : "bg-white/10"}`} />
                </div>
                <div className="mt-3 px-2 flex-1">
                  <EventCard event={ev} skin={skin} onDelete={canEdit ? onDelete : null} onVault={isOwner ? toggleVault : null} onReview={canEdit ? reviewEvent : null} onSource={openSource} horizontal />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={`flex-1 border-l-2 pl-6 ml-3 space-y-4 pt-2 ${skin.rail}`} data-testid="timeline-stream">
          {visible.map((ev) => (
            <div key={ev.id} className="relative">
              <div className="absolute -left-[33px] top-4 flex items-center justify-center w-4">
                <Marker event={ev} />
              </div>
              <EventCard event={ev} skin={skin} onDelete={canEdit ? onDelete : null} onVault={isOwner ? toggleVault : null} onReview={canEdit ? reviewEvent : null} onSource={openSource} />
            </div>
          ))}
        </div>
      )}

      <UndatedSection caseId={caseId} events={events} canEdit={canEdit} onChanged={onRefresh} onSource={(page, quote, documentId) => setSourceView({ documentId, page, quote })} />
      {sourceView && <SourceViewer documentId={sourceView.documentId} page={sourceView.page} quote={sourceView.quote} onClose={() => setSourceView(null)} />}
      {showAdd && <AddEventDialog caseId={caseId} onAdded={onEventAdded} onClose={() => setShowAdd(false)} />}
      {showSummary && <SummaryModal caseId={caseId} onClose={() => setShowSummary(false)} />}
      {vaultDialog && (
        <VaultDialog
          mode={vaultDialog}
          onUnlocked={(tok) => onVaultToken(tok)}
          onSetup={() => setPinSet(true)}
          onClose={() => setVaultDialog(null)}
        />
      )}
    </div>
  );
}
